import { Photo } from "@/lib/shared_types";

type PhotoAlbum = {
  id: number;
  title: string;
  photos_count: number;
  sample_photos: Photo[];
  created_at: string;
};

type TaggedPhotoAlbum = {
  title: string;
  photos_count: number;
  sample_photos: Photo[];
  athleteHandle: string;
};

type PhotoAlbumSummaryTabProps = {
  photoAlbums: PhotoAlbum[];
  taggedPhotoAlbum: TaggedPhotoAlbum;
  eventHandle: string;
};

export function PhotoAlbumSummaryTab({
  photoAlbums,
  taggedPhotoAlbum,
  eventHandle,
}: PhotoAlbumSummaryTabProps) {
  return (
    <div className="flex flex-col gap-6">
      <h3>Event Photos</h3>

      {photoAlbums.length === 0 && (
        <div className="flex items-center justify-center h-full">
          <span className="text-body font-body text-subtext-color">
            There are no photo albums for this event yet.
          </span>
        </div>
      )}

      {taggedPhotoAlbum && taggedPhotoAlbum.photos_count > 0 && (
        <TaggedPhotosCard album={taggedPhotoAlbum} eventHandle={eventHandle} />
      )}

      <div className="w-full flex flex-col gap-4">
        {photoAlbums.map((album) => (
          <PhotoAlbumCard
            key={album.id}
            album={album}
            eventHandle={eventHandle}
          />
        ))}
      </div>
    </div>
  );
}

PhotoAlbumSummaryTab.displayName = "PhotoAlbumSummaryTab";

function PhotoThumbnail({ photo }: { photo: Photo }) {
  if (!photo.sizes) {
    return null;
  }

  return (
    <div className="aspect-square overflow-hidden border border-neutral-200">
      <img
        src={photo.sizes["102x102crop"].url}
        className="h-full w-full object-cover"
      />
    </div>
  );
}

function PhotoCardSection({
  title,
  subtitle,
  photos,
  linkURL,
  linkLabel,
}: {
  title: string;
  subtitle: string;
  photos: Photo[];
  linkURL: string;
  linkLabel: string;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border-color p-4">
      <span className="text-heading-3 font-heading-3 text-default-font">
        {title}
      </span>
      <span className="text-body font-body text-subtext-color">
        {subtitle} • <a href={linkURL}>{linkLabel}</a>
      </span>

      {/* can't use tailwind for grid styling here because we're fighting webflow */}
      <div
        className="mt-4 gap-1"
        style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)" }}>
        {photos.slice(0, 16).map((photo, index) => (
          <PhotoThumbnail key={index} photo={photo} />
        ))}
      </div>
    </div>
  );
}

function PhotoAlbumCard({
  album,
  eventHandle,
}: {
  album: PhotoAlbum;
  eventHandle: string;
}) {
  return (
    <PhotoCardSection
      title={album.title}
      subtitle={pluralize(album.photos_count, "photo")}
      photos={album.sample_photos}
      linkURL={`/events/${eventHandle}/photo_albums/${album.id}`}
      linkLabel={"View Album →"}
    />
  );
}

function TaggedPhotosCard({
  album,
  eventHandle,
}: {
  album: TaggedPhotoAlbum;
  eventHandle: string;
}) {
  return (
    <PhotoCardSection
      title={album.title}
      subtitle={pluralize(album.photos_count, "photo")}
      photos={album.sample_photos}
      linkURL={`/events/${eventHandle}/tagged_photos/${album.athleteHandle}`}
      linkLabel={"View →"}
    />
  );
}

function pluralize(count: number, singular: string, plural?: string) {
  return `${count} ${count === 1 ? singular : plural || `${singular}s`}`;
}
